/**
 * InvestmentMap — F2: mapa inwestycji (2026-05-06).
 *
 * Leaflet załadowany dynamicznie z CDN unpkg (bez npm dep — prosty deploy).
 * OpenStreetMap tile (free, MIT). Markery kolor-coded po match_score.
 *
 * Use w stronach Pre-Tender / Tenders jako alternatywa do tabeli.
 * Akceptuje listę itemów z polami: lat, lon, source_id, title, match_score,
 *   match_label, deadline, contracting_authority, source.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { MapPin, RefreshCw, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { geocodeBackfill, getGeocodeStats } from '../../lib/api';

const LEAFLET_CSS_URL = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS_URL = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

// Poland centroid + zoom
const POLAND_CENTER = [52.0693, 19.4803];
const POLAND_ZOOM = 6;

// Score → color (consistent with score badges)
function scoreColor(score) {
  if (score == null) return '#6b7280';     // gray
  if (score >= 70) return '#10b981';        // emerald (high)
  if (score >= 40) return '#f59e0b';        // amber (medium)
  return '#71717a';                          // zinc (low)
}

function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  return new Promise((resolve, reject) => {
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS_URL;
      document.head.appendChild(link);
    }
    if (window.L) return resolve(window.L);
    const existing = document.getElementById('leaflet-js');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.L));
      existing.addEventListener('error', reject);
      return;
    }
    const script = document.createElement('script');
    script.id = 'leaflet-js';
    script.src = LEAFLET_JS_URL;
    script.async = true;
    script.onload = () => resolve(window.L);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

export default function InvestmentMap({ items = [], onMarkerClick, onBackfillDone, height = '600px' }) {
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const markersLayerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [stats, setStats] = useState(null);

  // Load coverage stats
  useEffect(() => {
    getGeocodeStats().then(setStats).catch(() => {});
  }, []);

  const handleBackfill = async () => {
    setBackfilling(true);
    try {
      toast.info('Geocoduję adresy z Nominatim... (~1 req/sec, max 50)');
      const r = await geocodeBackfill(50);
      toast.success(`Zgeokodowano ${r.geocoded} z ${r.scanned} adresów`);
      const s = await getGeocodeStats();
      setStats(s);
      if (onBackfillDone) onBackfillDone();
    } catch (e) {
      toast.error('Backfill nie powiódł się: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBackfilling(false);
    }
  };

  // Init map once
  useEffect(() => {
    let cancelled = false;
    loadLeaflet()
      .then(L => {
        if (cancelled || !containerRef.current) return;
        if (mapRef.current) return; // already init
        const map = L.map(containerRef.current, {
          center: POLAND_CENTER,
          zoom: POLAND_ZOOM,
          scrollWheelZoom: true,
          preferCanvas: true,
        });
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap',
          maxZoom: 18,
        }).addTo(map);
        mapRef.current = map;
        markersLayerRef.current = L.layerGroup().addTo(map);
        setLoading(false);
      })
      .catch(e => {
        console.error('Leaflet load failed:', e);
        setError('Nie udało się załadować mapy (Leaflet CDN). Spróbuj odświeżyć.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Fix Leaflet sizing after mount (container may have been hidden when Leaflet inited)
  useEffect(() => {
    if (mapRef.current && !loading) {
      const t = setTimeout(() => mapRef.current?.invalidateSize(true), 100);
      return () => clearTimeout(t);
    }
  }, [loading]);

  // Update markers when items change OR when map finishes loading
  // (depends on `loading` too — guards against race where items arrived
  //  before Leaflet was ready, which left the markers layer empty until
  //  items reference changed again)
  useEffect(() => {
    if (loading) return;
    if (!mapRef.current || !markersLayerRef.current || !window.L) return;
    const L = window.L;
    markersLayerRef.current.clearLayers();
    // Force resize sync first (in case container resized between renders)
    mapRef.current.invalidateSize(true);
    const valid = items.filter(it => it.lat != null && it.lon != null);
    valid.forEach(it => {
      const color = scoreColor(it.match_score);
      const score = it.match_score != null ? it.match_score : '?';
      const icon = L.divIcon({
        className: 'custom-marker',
        html: `<div style="background:${color};color:white;border:2px solid #fff;border-radius:50%;width:32px;height:32px;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:bold;box-shadow:0 2px 6px rgba(0,0,0,0.4);">${score}</div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
      });
      const marker = L.marker([it.lat, it.lon], { icon });
      const valueText = it.estimated_value
        ? new Intl.NumberFormat('pl-PL').format(it.estimated_value) + ' PLN'
        : 'wartość: —';
      const ca = Array.isArray(it.contracting_authority)
        ? it.contracting_authority[0]
        : (it.contracting_authority || '—');
      const popupHtml = `
        <div style="min-width:240px;font-family:system-ui,sans-serif">
          <div style="background:${color};color:white;font-size:11px;font-weight:bold;padding:2px 6px;border-radius:3px;display:inline-block;margin-bottom:4px">
            score ${score}/100 · ${(it.match_label || '').toUpperCase()}
          </div>
          <div style="font-weight:bold;font-size:13px;margin-bottom:4px">${(it.title || '—').replace(/</g, '&lt;')}</div>
          <div style="color:#666;font-size:11px;margin-bottom:2px">${ca}</div>
          <div style="color:#666;font-size:11px">${valueText}${it.deadline ? ' · termin: ' + it.deadline.slice(0, 10) : ''}</div>
          <a href="#" data-source-id="${it.source_id}" class="map-marker-link" style="display:inline-block;margin-top:8px;padding:4px 8px;background:#10b981;color:white;text-decoration:none;border-radius:3px;font-size:11px;">Otwórz tender</a>
        </div>`;
      marker.bindPopup(popupHtml);
      marker.on('popupopen', (e) => {
        // Wire up the link inside popup
        setTimeout(() => {
          const link = e.popup._contentNode?.querySelector('.map-marker-link');
          if (link) {
            link.addEventListener('click', (ev) => {
              ev.preventDefault();
              if (onMarkerClick) onMarkerClick(it.source_id);
            });
          }
        }, 0);
      });
      markersLayerRef.current.addLayer(marker);
    });

    // Auto-fit if markers exist
    if (valid.length > 0) {
      const bounds = L.latLngBounds(valid.map(it => [it.lat, it.lon]));
      mapRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 11 });
    }
  }, [items, onMarkerClick, loading]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-sm flex items-center gap-2">
            <MapPin className="w-4 h-4 text-primary" />
            Mapa inwestycji
            <Badge variant="outline">
              {items.filter(it => it.lat && it.lon).length} z {items.length} pokazane
            </Badge>
            {stats && (
              <Badge variant="outline" className="text-[10px]">
                pokrycie geo: {stats.coverage_pct}% ({stats.with_coords}/{stats.total})
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground hidden lg:inline">OpenStreetMap</span>
            <Button
              size="sm" variant="outline"
              onClick={handleBackfill} disabled={backfilling}
              title="Pobierz lat/lon dla brakujących z Nominatim (rate-limit 1req/s)"
            >
              {backfilling
                ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" />
                : <Sparkles className="w-3.5 h-3.5 mr-1" />}
              Geocode +50
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {loading && (
          <div className="flex items-center justify-center text-muted-foreground" style={{ height }}>
            <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Ładuję Leaflet z CDN...
          </div>
        )}
        {error && (
          <div className="flex items-center justify-center text-red-400 p-8">
            {error}
          </div>
        )}
        <div
          ref={containerRef}
          style={{
            height,
            width: '100%',
            display: loading || error ? 'none' : 'block',
            backgroundColor: '#0a0a0a',
          }}
        />
      </CardContent>
    </Card>
  );
}
